import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createTask } from '@/lib/api/generated/api';
import type { TaskView, WorkflowView } from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';
import { classifyMoveFailure, type CreateRefusal } from '../errors';
import { toTaskDescription, type TaskFormValues } from '../taskForm';
import { useOwnTaskIds } from '../ownTaskIds';

export interface CreateTaskInput {
  workspaceId: string;
  workflow: WorkflowView;
  values: TaskFormValues;
}

/**
 * A workflow declaring a required input has no path through this form
 * (spec FR-025) — this form collects only title and description. Checked
 * client-side, before any request, so the refusal reads as "not supported
 * yet" rather than a validation error in the user's data (research.md R7).
 * The server's own validation remains the backstop.
 */
export function requiredInputRefusal(
  workflow: WorkflowView,
): CreateRefusal | null {
  const required = workflow.inputs.filter((input) => input.required);
  if (required.length === 0) return null;
  const names = required.map((input) => input.name);
  return {
    kind: 'required-inputs',
    inputNames: names,
    message: `${names.join(', ')} — this workflow needs values for ${
      required.length === 1 ? 'an input' : 'inputs'
    } this form does not collect yet. Workflow inputs are not supported in task creation yet.`,
  };
}

export type CreateTaskResult =
  { task: TaskView; refusal: null } | { task: null; refusal: CreateRefusal };

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation<CreateTaskResult, Error, CreateTaskInput>({
    // No `position`: the service places a new task at its workflow's start
    // step (004 FR-006). The field is deliberately absent from the contract,
    // so a caller cannot seed a task mid-workflow by naming a step.
    mutationFn: async ({ workspaceId, workflow, values }) => {
      const response = await createTask(workspaceId, workflow.id, {
        title: values.title.trim(),
        description: toTaskDescription(values.description),
      });
      if (response.status === 201) {
        return { task: response.data, refusal: null };
      }
      // Mirrors useMoveTask's shape: a step with no linked AI profile is a
      // refusal to report, not a thrown error — reuses classifyMoveFailure's
      // classification since the backend's STEP_MISSING_LLM_PROFILE code and
      // envelope shape are identical on both endpoints (spec 122, contracts
      // §5). Any other 4xx is left as the pre-existing generic failure.
      const classified = classifyMoveFailure(response.status, response.data);
      if (classified.kind === 'missing-profile') {
        return { task: null, refusal: classified };
      }
      throw new Error('The task could not be created.');
    },
    onSuccess: (result, { workspaceId }) => {
      if (!result.task) return;
      const task = result.task;
      // The person made it, so it appears at once, never held back with
      // other arrivals and never washed as news (spec 148).
      useOwnTaskIds.getState().add(task.id);
      // Seed the new task into the aggregate so it renders at the start step
      // immediately. The create response already carries the authoritative
      // TaskView, so there is nothing a refetch would add — invalidating here
      // would only risk a same-key race stomping this seed with a response
      // that has not caught up yet. The next natural refetch (staleTime
      // elapsing, or a subsequent placement) reconciles as usual.
      queryClient.setQueryData(
        workspaceKeys.detail(workspaceId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object' || !('tasks' in prev))
            return prev;
          const aggregate = prev as { tasks: TaskView[] };
          return { ...aggregate, tasks: [...aggregate.tasks, task] };
        },
      );
    },
  });
}
